/**
 * The save record (spec §10, plan §6 M2-12): the Career plus the per-system world deltas. Saves hold seeds and
 * deltas only; untouched rocks, wrecks and markets regenerate from the career and field seeds. The world record
 * is keyed by system (`worlds.home` in M2) so M3 adds systems without a shape change (D-TBD: save format v1
 * keeps world deltas per system). Loads always resume docked at `career.lastDockedStation`.
 *
 * v2 (M3 plan R22k, §7 item 9) adds `reach`: the Reach state (knowledge, relays, deltas, charts, home, route). A v1
 * record migrates with a fresh Reach state for its galaxy seed (`migrations[1]`).
 *
 * v3 (5(e) MC-e5b, T32) is the ware re-stride: no new key. The price log's four rows grow from the 17-ware to the
 * 19-ware strategic layout (−1 = no data at the new slots), and every M2 ware record gains `refractory_ore: 0`
 * (`migrations[2]`). Rollback: an older build refuses a v3 record as `future` (`migrate`), so it never half-loads.
 *
 * `saveRecordSchema` is strict at every level: unknown keys, non-finite numbers and broken invariants fail, so
 * a corrupt save is named (slot and Zod path) instead of loaded.
 */
import { z } from 'zod'
import { cargoContentsSchema, careerSchema, makerIdSchema } from '../career/schema'
import type { Career } from '../career/types'
import { componentTypeSchema } from '../data/m2Schemas'
import { logger } from '../core/logger'
import { reachStateSchema } from '../reach/save/schema'
import type { ReachState } from '../reach/types'
import {
  WRECK_KIND_NAMES,
  type EncounterDeltas,
  type MarketState,
  type RockDeltas,
  type SalvageDeltas,
  type WorldDeltas,
  type WreckPartRecord,
  type WreckRecord,
} from '../space/contracts'

/** The current save format version (bump it together with a `migrations` entry). */
export const SAVE_VERSION = 3

/** A star system id (`stations.json systemId`; M2 has only `home`). */
export type SystemId = string

export interface SaveRecord {
  version: 3
  /** ISO time the record was written. */
  savedAt: string
  /** Game seconds (equals `career.gameTime`: the runtime clock is folded in when the record is built). */
  gameTime: number
  /** Galaxy seed (M3; absent in M2). */
  galaxySeed?: number
  career: Career
  worlds: Record<SystemId, WorldDeltas>
  /** The Reach state (v2, M3 R22k): M3's knowledge, relays, per-system deltas, charts, home and route. */
  reach: ReachState
}

// ---------------------------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------------------------

const nonNegative = z.number().nonnegative()
const count = z.number().int().nonnegative()
const unit = z.number().min(0).max(1)
const id = z.string().min(1)
const uint32 = z.number().int().min(0).max(0xffffffff)
const vec3 = z.tuple([z.number(), z.number(), z.number()])
const quat = z.tuple([z.number(), z.number(), z.number(), z.number()])

export const rockDeltasSchema: z.ZodType<RockDeltas> = z.strictObject({
  remaining: z.array(z.tuple([count, nonNegative])),
  removed: z.array(count),
  fractured: z.array(z.strictObject({ parent: count, fragments: z.array(z.tuple([count, nonNegative])) })),
})

export const wreckPartRecordSchema: z.ZodType<WreckPartRecord> = z.strictObject({
  type: componentTypeSchema,
  condition: unit,
  /** Salvaged Mk (economy plan SY-4a): 1-3, or 0 for a type without one; absent in older saves. */
  mk: z.number().int().min(0).max(3).optional(),
  /** Who made the part (M8 MC-f5a): written only where the seed can't re-derive it; absent in older saves. */
  maker: makerIdSchema.optional(),
  /** The Rush-pattern tag as rolled (M8 MC-h1, plumbed by MC-f5a): written only when set; absent in older saves. */
  rush: z.boolean().optional(),
  cut: z.array(unit),
  freed: z.boolean(),
  secured: z.boolean(),
  pos: vec3.optional(),
  vel: vec3.optional(),
  rot: quat.optional(),
  contents: cargoContentsSchema.optional(),
})

export const wreckRecordSchema: z.ZodType<WreckRecord> = z.strictObject({
  key: id,
  kind: z.enum(WRECK_KIND_NAMES),
  defId: id.optional(),
  section: id.optional(),
  seed: uint32,
  pos: vec3,
  vel: vec3,
  rot: quat,
  bornAt: nonNegative,
  lootChecked: z.boolean(),
  contractId: id.optional(),
  parts: z.array(wreckPartRecordSchema),
})

export const salvageDeltasSchema: z.ZodType<SalvageDeltas> = z.strictObject({
  nextSeq: count,
  wrecks: z.array(wreckRecordSchema),
})

export const encounterDeltasSchema: z.ZodType<EncounterDeltas> = z.strictObject({
  nextEncounterAt: nonNegative,
  repoDueAt: z.number().min(-1),
  groupSeq: count,
  groups: z.array(z.strictObject({ contractId: id, marker: vec3, tier: count })),
})

export const marketStateSchema: z.ZodType<MarketState> = z.record(
  id,
  z.record(id, z.strictObject({ s: nonNegative, t: nonNegative })),
)

export const worldDeltasSchema: z.ZodType<WorldDeltas> = z.strictObject({
  rocks: rockDeltasSchema,
  salvage: salvageDeltasSchema,
  markets: marketStateSchema,
  encounters: encounterDeltasSchema,
})

export const saveRecordSchema: z.ZodType<SaveRecord> = z.strictObject({
  version: z.literal(SAVE_VERSION),
  savedAt: z.iso.datetime(),
  gameTime: nonNegative,
  galaxySeed: uint32.optional(),
  career: careerSchema,
  worlds: z.record(id, worldDeltasSchema),
  reach: reachStateSchema,
})

/** "career.cargo.wares.ice: Too small: expected number to be >=0" (the first `max` issues, then "+N more"). */
export function zodIssueMessage(error: z.ZodError, max = 3): string {
  const lines = error.issues.slice(0, max).map((issue) => `${formatPath(issue.path)}: ${issue.message}`)
  const extra = error.issues.length - max
  return extra > 0 ? `${lines.join('; ')}; +${extra} more` : lines.join('; ')
}

function formatPath(path: readonly PropertyKey[]): string {
  if (path.length === 0) return '(root)'
  let out = ''
  for (const part of path) {
    if (typeof part === 'number') out += `[${part}]`
    else out += out === '' ? String(part) : `.${String(part)}`
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Building and measuring
// ---------------------------------------------------------------------------------------------

export interface BuildRecordInput {
  career: Career
  /** The runtime game clock (folded into the career). Default: `career.gameTime`. */
  gameTime?: number
  worlds: Record<SystemId, WorldDeltas>
  /** The Reach state (kept by reference: pass a snapshot). */
  reach: ReachState
  galaxySeed?: number
  /** Wall-clock time of the save. Default: now. */
  now?: Date
}

export function buildRecord(input: BuildRecordInput): SaveRecord {
  const gameTime = input.gameTime ?? input.career.gameTime
  const career = input.career.gameTime === gameTime ? input.career : { ...input.career, gameTime }
  const record: SaveRecord = {
    version: SAVE_VERSION,
    savedAt: (input.now ?? new Date()).toISOString(),
    gameTime,
    career,
    worlds: input.worlds,
    reach: input.reach,
  }
  if (input.galaxySeed !== undefined) record.galaxySeed = input.galaxySeed
  return record
}

/** The stored form: compact JSON. */
export function serializeRecord(record: SaveRecord): string {
  return JSON.stringify(record)
}

const encoder = new TextEncoder()

function utf8Bytes(text: string): number {
  return encoder.encode(text).length
}

/** UTF-8 size of the stored form. */
export function recordBytes(record: SaveRecord): number {
  return utf8Bytes(serializeRecord(record))
}

/** Bytes per section (`career.lease`, `worlds.home.rocks`, ...), largest first. */
export function recordSections(record: SaveRecord): { section: string; bytes: number }[] {
  const out: { section: string; bytes: number }[] = []
  for (const [key, value] of Object.entries(record.career)) {
    out.push({ section: `career.${key}`, bytes: utf8Bytes(JSON.stringify(value)) })
  }
  for (const [system, world] of Object.entries(record.worlds)) {
    for (const [key, value] of Object.entries(world)) {
      out.push({ section: `worlds.${system}.${key}`, bytes: utf8Bytes(JSON.stringify(value)) })
    }
  }
  return out.sort((a, b) => b.bytes - a.bytes)
}

/**
 * Logs a warning naming the largest sections when a record is over `maxBytes` (`career.json save.maxBytes`).
 * The record is still saved. Returns its size.
 */
export function checkRecordSize(record: SaveRecord, maxBytes: number): number {
  const bytes = recordBytes(record)
  if (bytes > maxBytes) {
    logger.warn(`save: record is ${bytes} B, over the ${maxBytes} B target`, { largest: recordSections(record).slice(0, 5) })
  }
  return bytes
}
